import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import React, { createRef, useEffect, useState } from 'react';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  StyledEngineProvider,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { isEmpty } from 'lodash';
import { Location } from '../../interfaces/Location';
import {
  sharedColors,
  timePickerTheme,
  useSharedStyles,
} from '../../utilities/Styles';
import { getDefaultBatchNormalizationSettings } from '../../utilities/Settings/BatchNormalizationSettings';
import { createBatchNormalizationSettings } from '../../services/settings';
import { handleCountryLocationID } from '../../utilities/Settings/Settings';
import Waiting from '../Waiting';
import { multiClass } from '../../utilities/Extensions';
import MultiLocationAutocomplete from '../MultiLocationAutocomplete';
import { getNextTuesday, isFutureTuesday } from '../../utilities/Dates';
import BatchNormalizationNumberInput from './BatchNormalizationNumberInput';

interface NewBatchNormalizationSettingsDialogProps {
  locations: Location[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

const NewBatchNormalizationSettingsDialog = (
  props: NewBatchNormalizationSettingsDialogProps,
) => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles().classes;

  const validatorRef = createRef<ValidatorForm>();

  const [loading, setLoading] = useState(false);
  const [currentState, setCurrentState] = useState(
    getDefaultBatchNormalizationSettings(),
  );
  const [filteredLocationIDs, setFilteredLocationIDs] = useState<string[]>([]);
  const [invalidSettings, setInvalidSettings] = useState(false);

  const resetState = () => {
    setCurrentState(getDefaultBatchNormalizationSettings());
    setFilteredLocationIDs([]);
  };

  useEffect(resetState, [props.open]);

  useEffect(() => {
    validatorRef.current?.isFormValid(false).then((isValid) => {
      setInvalidSettings(!isValid);
    });
  }, [currentState, filteredLocationIDs]);

  const handleSubmit = () => {
    setLoading(true);
    createBatchNormalizationSettings(
      filteredLocationIDs.map((locationID) =>
        handleCountryLocationID(locationID),
      ),
      currentState,
    )
      .then(() => {
        toast.success('New settings are successfully created.');
        props.onDone();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth='md'>
      <div style={{ width: 700 }}>
        <Waiting open={loading} />
        <ValidatorForm
          instantValidate
          onSubmit={handleSubmit}
          onError={() => setInvalidSettings(true)}
          ref={validatorRef}
        >
          <DialogTitle
            className={multiClass([sharedClasses.h6, classes.title])}
          >
            New Batch Normalization Settings
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Box component='div' className={classes.dialogContent}>
              <Box component='div' className={classes.row}>
                <MultiLocationAutocomplete
                  label='Location Name'
                  locations={props.locations.slice(0, 2)}
                  selectedIDs={filteredLocationIDs}
                  onSetSelectedIDs={setFilteredLocationIDs}
                />
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={timePickerTheme}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label='Start Date'
                        value={currentState.startAt}
                        disabled={isEmpty(filteredLocationIDs)}
                        shouldDisableDate={(date) => !isFutureTuesday(date)}
                        onChange={(newStartAt) =>
                          setCurrentState({
                            ...currentState,
                            startAt: newStartAt ?? getNextTuesday(),
                          })
                        }
                        renderInput={(
                          params: JSX.IntrinsicAttributes & TextFieldProps,
                        ) => (
                          <TextField
                            {...params}
                            size='small'
                            className={classes.datePicker}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                </StyledEngineProvider>
              </Box>
              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid item xs={4}>
                  <BatchNormalizationNumberInput
                    currentState={currentState}
                    setter={setCurrentState}
                    fieldName={'1'}
                    disabled={isEmpty(filteredLocationIDs)}
                    placeholder='Singleton'
                    minValue={-1}
                    maxValue={1}
                  />
                </Grid>
                <Grid item xs={4}>
                  <BatchNormalizationNumberInput
                    currentState={currentState}
                    setter={setCurrentState}
                    fieldName={'2'}
                    disabled={isEmpty(filteredLocationIDs)}
                    placeholder='Batch of 2'
                    minValue={-1}
                    maxValue={1}
                  />
                </Grid>
                <Grid item xs={4}>
                  <BatchNormalizationNumberInput
                    currentState={currentState}
                    setter={setCurrentState}
                    fieldName={'3'}
                    disabled={isEmpty(filteredLocationIDs)}
                    placeholder='Batch of 3'
                    minValue={-1}
                    maxValue={1}
                  />
                </Grid>
                <Grid item xs={4}>
                  <BatchNormalizationNumberInput
                    currentState={currentState}
                    setter={setCurrentState}
                    fieldName={'4'}
                    disabled={isEmpty(filteredLocationIDs)}
                    placeholder='Batch of 4'
                    minValue={-1}
                    maxValue={1}
                  />
                </Grid>
                <Grid item xs={4}>
                  <BatchNormalizationNumberInput
                    currentState={currentState}
                    setter={setCurrentState}
                    fieldName={'5'}
                    disabled={isEmpty(filteredLocationIDs)}
                    placeholder='Batch of 5+'
                    minValue={-1}
                    maxValue={1}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box component='div' className={classes.actionButtonDivider} />
            <Button
              variant='contained'
              color='inherit'
              onClick={props.onClose}
              className={sharedClasses.buttonText}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={invalidSettings || isEmpty(filteredLocationIDs)}
              type='submit'
              className={sharedClasses.buttonText}
            >
              Confirm
            </Button>
          </DialogActions>
        </ValidatorForm>
      </div>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) =>
  createStyles({
    title: {
      color: sharedColors.gray6,
    },
    dialogContent: {
      flexDirection: 'column',
    },
    actionButtonDivider: {
      flex: '1 0 0',
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      flexGrow: 1,
    },
    datePicker: {
      width: 170,
      marginLeft: theme.spacing(1.5),
    },
  }),
);

export default NewBatchNormalizationSettingsDialog;
